import React from 'react';
import {
  FaDatabase,
  FaSearch,
  FaBoxes,
  FaTrophy,
  FaLaptopCode,
  FaClock,
  FaChartLine
} from 'react-icons/fa';

export const analyticsMetadata = {
  id: 'analytics',
  title: "Activation & Early Engagement",
  company: "UrbanGo",
  category: "Product Analytics",
  cardDescription: "Dive into user behavior data for a city mobility app. Measure activation, retention, and engagement funnels.",
  learningObjectives: [
    "Funnel Analysis",
    "Retention Metrics",
    "Cohort Analysis",
    "Time-Series Trends"
  ],
  scenario: (
    <div className="space-y-8">
      {/* Hero Section */}
      <div className="bg-white dark:bg-[#0f172a] rounded-xl border border-gray-100 dark:border-gray-800 p-8 shadow-sm relative overflow-hidden group hover:border-purple-500/20 transition-all duration-500">
        <div className="relative z-10">
          <h3 className="text-3xl font-bold text-gray-900 dark:text-white mb-4 tracking-tight">
            UrbanGo <span className="text-transparent bg-clip-text bg-gradient-to-r from-purple-600 to-indigo-600">Analytics</span>
          </h3>

          <p className="text-gray-600 dark:text-gray-300 leading-relaxed text-lg max-w-3xl font-light">
            You've joined <strong className="text-gray-900 dark:text-white font-semibold">UrbanGo</strong> as a Product Data Analyst.
            Your manager Priya needs to understand the activation funnel: Are new users finding value quickly? Which channels bring quality users?
          </p>
        </div>
        {/* Subtle Brand Accent */}
        <div className="absolute top-0 right-0 w-32 h-32 bg-gradient-to-br from-purple-500/5 to-indigo-500/5 rounded-full blur-2xl -mr-10 -mt-10"></div>
      </div>

      {/* Schema Grid */}
      <div>
        <h4 className="text-xs font-bold uppercase tracking-widest text-gray-400 dark:text-gray-500 mb-4 pl-1">
          Data Architecture
        </h4>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          {[
            { table: 'users', desc: 'Who signed up (channel, device, timestamp)' },
            { table: 'sessions', desc: 'When they opened the app' },
            { table: 'events', desc: 'What they did (viewed, saved, navigated)' }
          ].map((item) => (
            <div key={item.table} className="bg-gray-50 dark:bg-[#162032] p-4 rounded-lg border border-gray-100 dark:border-gray-800 hover:border-gray-300 dark:hover:border-gray-600 transition-colors">
              <div className="font-mono font-bold text-purple-600 dark:text-purple-400 text-sm mb-1">
                {item.table}
              </div>
              <div className="text-xs text-gray-500 dark:text-gray-400">
                {item.desc}
              </div>
            </div>
          ))}
        </div>
      </div>
    </div>
  ),
  introduction: ``,
  hasSQL: true
};

export const analyticsChallenges = [
  {
    id: 'daily-signup-trends',
    title: 'Daily Signup Trends',
    question: `- Using the \`users\` table, count signups grouped by \`acquisition_channel\` and \`device_type\`
- Return \`signup_date\`, \`acquisition_channel\`, \`device_type\`, and \`users_count\`, ordered by most recent date first`,

    keyConcepts: ['DATE()', 'GROUP BY', 'COUNT()', 'multiple dimensions'],
    defaultQuery: `-- TIP: DATE(timestamp) extracts just the date part from a datetime
-- GROUP BY DATE(signup_at), acquisition_channel`,
    expectedColumns: [
      { name: 'signup_date', type: 'DATE' },
      { name: 'acquisition_channel', type: 'TEXT' },
      { name: 'device_type', type: 'TEXT' },
      { name: 'users_count', type: 'INTEGER' },
    ],
    businessRequest: `Priya needs a daily breakdown of new signups by acquisition channel and device type to understand which sources are growing and whether mobile or desktop is dominant.`,
    concept: `**Multi-Dimensional Grouping**

When you group by a single column you get one row per unique value. Group by two columns and you get one row per unique *combination* - every possible pairing, each with its own count.

\`\`\`sql
GROUP BY country, device_type
-- one row per (country + device_type) pair
\`\`\`

Timestamps have a gotcha: two signups at 09:01 and 09:47 on the same day are different timestamps, so they'd fall into separate groups. Wrap the column in \`DATE()\` to strip the time and collapse everything from the same day into one key.

\`\`\`sql
GROUP BY DATE(created_at), channel
\`\`\`

**Rule:** every column you \`SELECT\` that isn't wrapped in an aggregate (\`COUNT\`, \`SUM\`, etc.) must appear in \`GROUP BY\`.`,
    context: `**The Business Request**

Priya needs to understand signup trends: who's signing up and how they're finding UrbanGo. This data reveals channel growth, device preferences, and seasonal patterns.

**The Concept**

Multi-dimensional grouping breaks down a metric by multiple categories simultaneously. You need:

- \`DATE()\` to extract date from timestamp
- \`GROUP BY\` multiple columns
- \`COUNT(*)\` to count per group

**The Syntax**

\`\`\`sql
SELECT DATE(timestamp_col) AS date, category1, category2, COUNT(*) AS count
FROM table
GROUP BY DATE(timestamp_col), category1, category2
ORDER BY date DESC
\`\`\`

<div style="background: #f0f9ff; border-left: 4px solid #3b82f6; padding: 16px; margin: 16px 0; border-radius: 4px;">

**Your Task:**

Show daily signups broken down by \`acquisition_channel\` and \`device_type\`. Order by date (newest first).

</div>`,
    hint: `**Approach:**

1. Extract date from \`signup_at\` using \`DATE()\`
2. Group by date, channel, and device
3. Count rows in each group
4. Order by date (newest first)

**Structure:**
- SELECT: date, channel, device, count
- FROM: users
- GROUP BY: all non-aggregated columns
- ORDER BY: date DESC`,
    solution: `SELECT 
  DATE(signup_at) AS signup_date,
  acquisition_channel,
  device_type,
  COUNT(*) AS users_count
FROM users
GROUP BY DATE(signup_at), acquisition_channel, device_type
ORDER BY signup_date DESC`,
    tip: `When grouping by multiple dimensions, always include ALL non-aggregated columns in GROUP BY. Missing one causes SQL errors or incorrect results!`,
    explanation: `**Signup Patterns Revealed!**

**What Just Happened:**

1. **DATE(signup_at):** Extract date from timestamp
2. **GROUP BY date, channel, device:** Multi-dimensional breakdown
3. **COUNT(*):** Users per segment
4. **ORDER BY signup_date DESC:** Most recent first

**How Multi-Dimensional Grouping Works:**

- **Date:** Growth trends, seasonality
- **Channel:** Which marketing works
- **Device:** Platform preferences

**Product Insights:**

- **Paid Android spikes weekends:** Mobile ad targeting effective
- **Organic iOS grows steadily:** Strong App Store presence
- **Referral small but consistent:** Word of mouth working

**Pro Tip:** Always include ALL non-aggregated columns in GROUP BY. Missing one causes SQL errors or incorrect results!`,
    concepts: [
      {
        icon: <FaChartLine className="text-3xl text-[#2563eb]" />,
        title: 'Date Extraction',
        description: 'Use DATE() to extract just the date from timestamps. Essential for daily/weekly/monthly trend analysis.'
      },
      {
        icon: <FaBoxes className="text-3xl text-[#f97316]" />,
        title: 'Multi-Dimensional Grouping',
        description: 'GROUP BY multiple columns to break down metrics by different dimensions. Reveals patterns hidden in aggregate numbers.'
      }
    ]
  },
  {
    id: 'time-to-first-session',
    title: 'Time to First App Usage',
    question: `- Join \`users\` to \`sessions\` and find each user's earliest session
- Include only users who have at least one session
- Return \`user_id\`, \`signup_at\`, \`first_session_start\`, and \`minutes_to_first_session\` (minutes between signup and first session)`,

    keyConcepts: ['MIN()', 'INNER JOIN', 'date_diff()', 'time arithmetic'],
    defaultQuery: `-- TIP: Use MIN(session_start) to find each user's first session
-- Then DATEDIFF('day', signup_at, first_session) measures activation lag`,
    expectedColumns: [
      { name: 'user_id', type: 'TEXT' },
      { name: 'signup_at', type: 'TIMESTAMP' },
      { name: 'first_session_start', type: 'TIMESTAMP' },
      { name: 'minutes_to_first_session', type: 'INTEGER' },
    ],
    businessRequest: `Priya wants to measure how quickly new users open the app after signing up. Fast time-to-first-session indicates strong product-market fit and effective onboarding.`,
    concept: `**MIN() and date_diff**

**\`MIN()\` as an aggregate** works just like \`COUNT\` or \`SUM\` - it runs once per group and returns a single value. When the column contains timestamps, that value is the earliest one.

\`\`\`sql
MIN(session_start)  -- earliest session in each group
\`\`\`

**\`date_diff\`** measures the gap between two timestamps. The first argument is the unit you want back:

\`\`\`sql
date_diff('minute', earlier_time, later_time)  -- whole minutes between the two
date_diff('hour',   earlier_time, later_time)  -- whole hours
date_diff('day',    earlier_time, later_time)  -- whole days
\`\`\`

You can pass an aggregate directly as one of the arguments - no subquery needed.

**JOIN note:** an \`INNER JOIN\` only keeps rows with a match on both sides. Any user with no sessions will be silently dropped. Think about whether that's the right behaviour here.`,
    context: `**The Business Request**

Priya wants to measure engagement speed: How quickly do new users open the app after signing up? Fast time-to-first-session indicates strong value prop and user intent.

**The Concept**

Use \`MIN()\` to find the earliest session per user, then \`date_diff()\` to calculate the time gap between signup and first session.

**The Syntax**

\`\`\`sql
SELECT user_id, signup_at,
  MIN(session_start) AS first_session,
  date_diff('minute', signup_at, MIN(session_start)) AS minutes
FROM users
JOIN sessions ON users.user_id = sessions.user_id
GROUP BY user_id, signup_at
\`\`\`

<div style="background: #f0f9ff; border-left: 4px solid #3b82f6; padding: 16px; margin: 16px 0; border-radius: 4px;">

**Your Task:**

For each user, show \`user_id\`, \`signup_at\`, \`first_session_start\`, and \`minutes_to_first_session\`.

</div>`,
    hint: `**Approach:**

1. JOIN users to sessions
2. Use MIN(session_start) to find first session per user
3. Calculate time difference:
   - date_diff('minute', signup, first_session)
4. GROUP BY user_id

**Remember:** INNER JOIN only includes users who had at least one session.`,
    solution: `SELECT 
  users.user_id,
  users.signup_at,
  MIN(sessions.session_start) AS first_session_start,
  date_diff('minute', users.signup_at, MIN(sessions.session_start)) AS minutes_to_first_session
FROM users
INNER JOIN sessions ON users.user_id = sessions.user_id
GROUP BY users.user_id, users.signup_at`,
    tip: `date_diff('minute', start, end) calculates the number of minute boundaries crossed. It's the standard way to measure duration in modern SQL!`,
    explanation: `**Engagement Speed Measured!**

**What Just Happened:**

1. **JOIN users to users:** Connect signup to activity
2. **MIN(session_start):** Find first session
3. **date_diff('minute', ...):** Calculate time gap

**How date_diff Works:**

\`\`\`sql
date_diff('minute', signup_at, first_session_start)
-- Returns: minutes between signup and first app open
\`\`\`

**Interpreting Time-to-First-Session:**

- **< 5 min:** Signed up IN app (✅ Great onboarding)
- **5-60 min:** Same session intent (✅ Strong interest)
- **1-24 hours:** Returned same day (⚠️ Acceptable)
- **> 24 hours:** Delayed engagement (❌ Activation risk)

**Benchmark:** Best apps see 50%+ of users return within 1 hour.

**If median > 24 hours:**
- Email onboarding might be weak
- App store messaging unclear
- Need push notification strategy`,
    concepts: [
      {
        icon: <FaClock className="text-3xl text-[#2563eb]" />,
        title: 'Time Difference',
        description: 'Use date_diff(\'minute\', start, end) to calculate duration. Much simpler than extracting epoch timestamps or using Julian days.'
      }, {
        icon: <FaDatabase className="text-3xl text-[#10b981]" />,
        title: 'MIN() with GROUP BY',
        description: 'MIN() finds the earliest value per group. Perfect for "first session", "first purchase", "first event" analysis.'
      }
    ]
  },
  {
    id: 'first-session-engagement',
    title: 'First Session Engagement',
    question: `- Using \`sessions\` and \`events\`, identify each user's first session (earliest \`session_start\`)
- Count events during that session - include users whose first session had zero events
- Return \`user_id\`, \`first_session_id\`, and \`events_in_first_session\``,

    keyConcepts: ['subquery', 'MIN()', 'COUNT()', 'JOIN'],
    defaultQuery: `-- TIP: JOIN users to sessions, then filter for each user's first session
-- Use a subquery or CTE: SELECT user_id, MIN(session_start) AS first_session ...`,
    expectedColumns: [
      { name: 'user_id', type: 'TEXT' },
      { name: 'first_session_id', type: 'TEXT' },
      { name: 'events_in_first_session', type: 'INTEGER' },
    ],
    businessRequest: `Priya wants to understand first-session quality: do new users explore features, or do they open the app and immediately leave? Low event counts signal onboarding or UX friction.`,
    concept: `**CTEs and the two-column JOIN trick**

A CTE (\`WITH ...\`) names an intermediate result so you can reference it like a table. It's useful when you need to compute something first, then join on that result.

\`\`\`sql
WITH earliest AS (
  SELECT user_id, MIN(started_at) AS first_time
  FROM sessions
  GROUP BY user_id
)
-- now join 'earliest' back to sessions ...
\`\`\`

Here's the key insight: if you join back to \`sessions\` on \`user_id\` alone, you match *every* session for each user - defeating the point. You need to match on **both** \`user_id\` and the exact timestamp to land on one specific row.

\`\`\`sql
JOIN sessions s
  ON earliest.user_id   = s.user_id
 AND earliest.first_time = s.started_at
\`\`\`

**LEFT vs INNER** for the events join: if some sessions have zero events, \`INNER JOIN\` would silently drop those sessions. \`LEFT JOIN\` keeps them and returns \`NULL\` for the missing side - which you then count as zero.`,
    context: `**The Business Request**

Priya wants to understand first session quality: Do users explore features or bounce immediately?

**The Concept**

Use a CTE to find each user's first session, then count events in that specific session. This pattern is essential for "first X" analysis.

**The Syntax**

\`\`\`sql
WITH first_sessions AS (
  SELECT user_id, MIN(session_start) AS first_start
  FROM sessions GROUP BY user_id
)
SELECT s.user_id, s.session_id, COUNT(e.event_id) AS events
FROM first_sessions fs
JOIN sessions s ON fs.user_id = s.user_id AND fs.first_start = s.session_start
LEFT JOIN events e ON s.session_id = e.session_id
GROUP BY s.user_id, s.session_id
\`\`\`

<div style="background: #f0f9ff; border-left: 4px solid #3b82f6; padding: 16px; margin: 16px 0; border-radius: 4px;">

**Your Task:**

For each user, show \`user_id\`, \`first_session_id\`, and \`events_in_first_session\`.

</div>`,
    hint: `**Step-by-step:**

1. Create CTE to find first session timestamp per user
2. JOIN back to sessions to get session_id
3. LEFT JOIN events (some sessions might have 0 events)
4. COUNT events per session
5. GROUP BY user_id, session_id

**Key:** Match on both user_id AND session_start to identify the exact first session.`,
    solution: `WITH first_sessions AS (
  SELECT 
    user_id,
    MIN(session_start) AS first_session_start
  FROM sessions
  GROUP BY user_id
)
SELECT 
  sessions.user_id,
  sessions.session_id AS first_session_id,
  COUNT(events.event_id) AS events_in_first_session
FROM first_sessions
INNER JOIN sessions ON first_sessions.user_id = sessions.user_id 
  AND first_sessions.first_session_start = sessions.session_start
LEFT JOIN events ON sessions.session_id = events.session_id
GROUP BY sessions.user_id, sessions.session_id`,
    tip: `Use LEFT JOIN for events because some sessions might have zero events. INNER JOIN would exclude those sessions entirely!`,
    explanation: `**First Session Quality Analyzed!**

**What Just Happened:**

1. **CTE: first sessions:** Identify each user's first session
2. **JOIN events:** Count events in that session
3. **LEFT JOIN:** Include sessions with 0 events

**How CTE + LEFT JOIN Works:**

\`\`\`sql
WITH first_sessions AS (...)  -- Find first session per user
SELECT ...
LEFT JOIN events ON ...       -- Include sessions with 0 events
\`\`\`

**Event Count Interpretation:**

- **0 events:** App opened, nothing happened (🚨 Crash? Confusion?)
- **1-2 events:** Minimal exploration (⚠️ Low engagement)
- **3-5 events:** Healthy engagement (✅ Normal)
- **6+ events:** Power user from day 1 (🌟 High value)

**Benchmark:** Successful apps see 5+ events in 50% of first sessions.

**If most users have < 3 events:**
- Onboarding unclear
- Core value prop not obvious
- UX friction preventing exploration`,
    concepts: [
      {
        icon: <FaLaptopCode className="text-3xl text-[#2563eb]" />,
        title: 'CTEs for Clarity',
        description: 'Use WITH (Common Table Expressions) to break complex queries into readable steps. First find first sessions, then analyze them.'
      },
      {
        icon: <FaSearch className="text-3xl text-[#f97316]" />,
        title: 'Multi-Condition JOINs',
        description: 'JOIN on multiple conditions (user_id AND timestamp) to identify specific records. Essential for "first X" analysis.'
      }
    ]
  },
  {
    id: 'activation-metric',
    title: 'Activation Metric (Core Question)',
    question: `- Join \`users\` to \`events\`; a user is **activated** if they triggered \`place_viewed\`, \`place_saved\`, or \`navigation_started\` within 24 hours of \`signup_at\`
- Return \`total_users\`, \`activated_users\`, and \`activation_rate\` rounded to 1 decimal place`,

    keyConcepts: ['CASE WHEN', 'time windows', 'product metrics', 'date_diff()'],
    defaultQuery: `-- TIP: COUNT(CASE WHEN condition THEN 1 END) counts activated users
-- Divide by COUNT(*) to get an activation rate`,
    expectedColumns: [
      { name: 'total_users', type: 'INTEGER' },
      { name: 'activated_users', type: 'INTEGER' },
      { name: 'activation_rate', type: 'DECIMAL' },
    ],
    businessRequest: `Priya needs the core product metric: what percentage of new users experience the app's value within 24 hours of signing up? This activation rate predicts long-term retention.`,
    concept: `**Conditional counting with CASE WHEN inside COUNT**

\`COUNT(*)\` counts every row. You can make it selective by placing a \`CASE WHEN\` inside it: return the value when the condition is true, return \`NULL\` when it's false. \`COUNT\` ignores \`NULL\`s, so only the matching rows are tallied.

\`\`\`sql
COUNT(CASE WHEN status = 'paid' THEN 1 END)  -- counts only paid rows
\`\`\`

Pair with \`DISTINCT\` when your join can produce duplicate user rows and you only want to count each user once:

\`\`\`sql
COUNT(DISTINCT CASE WHEN condition THEN user_id END)
\`\`\`

**Denominator matters:** if you \`INNER JOIN\` to events, users with no events disappear - so your "total users" would only count users who had at least one event. A \`LEFT JOIN\` keeps everyone in the denominator, which is what you need for a true rate.

For the percentage: \`ROUND(100.0 * numerator / denominator, 1)\` gives a clean one-decimal result. Use \`100.0\` (not \`100\`) to force decimal division.`,
    context: `**The Business Request**

Priya needs THE core metric: What percentage of users experience value within 24 hours? This is activation rate.

**The Concept**

Activation = user triggered \`place_viewed\`, \`place_saved\`, or \`navigation_started\` within 24 hours of signup.

**The Syntax**

\`\`\`sql
SELECT 
  COUNT(DISTINCT users.user_id) AS total,
  COUNT(DISTINCT CASE 
    WHEN event_name IN ('place_viewed','place_saved','navigation_started')
      AND date_diff('hour', signup_at, event_time) <= 24
    THEN users.user_id END) AS activated
FROM users LEFT JOIN events ON ...
\`\`\`

<div style="background: #f0f9ff; border-left: 4px solid #3b82f6; padding: 16px; margin: 16px 0; border-radius: 4px;">

**Your Task:**

Calculate \`total_users\`, \`activated_users\`, and \`activation_rate\` (rounded to 1 decimal).

</div>`,
    hint: `**Approach:**

1. LEFT JOIN users to events (keep all users)
2. Use CASE WHEN to identify activated users:
   - Event is activation event (IN clause)
   - Event happened within 24 hours \`date_diff('hour', signup, event) <= 24\`
3. COUNT DISTINCT for both total and activated
4. Calculate percentage: (activated / total * 100)

**Key:** Use LEFT JOIN so users without events are still counted in total.`,
    solution: `SELECT 
  COUNT(DISTINCT users.user_id) AS total_users,
  COUNT(DISTINCT CASE 
    WHEN events.event_name IN ('place_viewed', 'place_saved', 'navigation_started')
      AND date_diff('hour', users.signup_at, events.event_time) <= 24
    THEN users.user_id 
  END) AS activated_users,
  ROUND(100.0 * COUNT(DISTINCT CASE 
    WHEN events.event_name IN ('place_viewed', 'place_saved', 'navigation_started')
      AND date_diff('hour', users.signup_at, events.event_time) <= 24
    THEN users.user_id 
  END) / COUNT(DISTINCT users.user_id), 1) AS activation_rate
FROM users
LEFT JOIN events ON users.user_id = events.user_id`,
    tip: `The 24-hour window is critical! Use date_diff('hour', start, end) <= 24 to include events exactly 24 hours later. Product definitions matter!`,
    explanation: `**Core Product Metric Defined!**

**What Just Happened:**

1. **LEFT JOIN events:** Include users with no events
2. **date_diff <= 24 hours:** Filter to activation window
3. **Event type check:** Identify value moment
4. **CASE WHEN + COUNT:** Calculate activation rate

**How Activation Rate Works:**

\`\`\`sql
100.0 * COUNT(DISTINCT CASE WHEN activated THEN user_id END)
      / COUNT(DISTINCT user_id)   -- Activation rate %
\`\`\`

**Activation Rate Benchmarks:**

- **< 20%:** 🚨 Critical problem (Users don't see value)
- **20-40%:** ⚠️ Needs improvement (Onboarding/UX issues)
- **40-60%:** ✅ Healthy (Good product-market fit)
- **> 60%:** 🌟 Excellent (Strong value prop)

**Why This Matters:**

Activation predicts retention better than any other metric. Users who activate are **5-10x more likely** to become long-term users.

**If activation is low:** Improve onboarding, make core features discoverable, add contextual tooltips.`,
    concepts: [
      {
        icon: <FaTrophy className="text-3xl text-[#f97316]" />,
        title: 'Activation Definition',
        description: 'Define activation as experiencing core value within a time window. This is THE metric that predicts retention and long-term success.'
      },
      {
        icon: <FaClock className="text-3xl text-[#2563eb]" />,
        title: '24-Hour Time Windows',
        description: 'Use date_diff(\'hour\', start, end) <= 24 for activation windows. Much cleaner than manual date arithmetic or timestamp comparisons.'
      }]
  },
  {
    id: 'activation-by-channel',
    title: 'Activation by Acquisition Channel',
    question: `- Same activation definition as the previous challenge: \`place_viewed\`, \`place_saved\`, or \`navigation_started\` within 24 hours of signup
- Break results down by \`acquisition_channel\`
- Return \`acquisition_channel\`, \`total_users\`, \`activated_users\`, and \`activation_rate\`, ordered from highest to lowest rate`,

    keyConcepts: ['GROUP BY', 'segmentation', 'CASE WHEN', 'channel analysis'],
    defaultQuery: `-- TIP: GROUP BY acquisition_channel to segment results by channel
-- ROUND(activated * 100.0 / total, 1) gives a clean percentage`,
    expectedColumns: [
      { name: 'acquisition_channel', type: 'TEXT' },
      { name: 'total_users', type: 'INTEGER' },
      { name: 'activated_users', type: 'INTEGER' },
      { name: 'activation_rate', type: 'DECIMAL' },
    ],
    businessRequest: `Priya wants to know which acquisition channels deliver higher-quality users. Not all channels are equal - some may bring volume but low engagement.`,
    concept: `**Segmenting an existing metric**

You already wrote the activation logic in the previous challenge. Segmenting it by channel isn't a new technique - it's just adding one column to your \`SELECT\` and \`GROUP BY\`. The metric definition stays exactly the same.

\`\`\`sql
GROUP BY acquisition_channel
ORDER BY activation_rate DESC
\`\`\`

**A note on interpreting results:** rate and volume tell different stories. A channel with 75% activation on 8 users is a curiosity. A channel with 45% activation on 800 users is a strategic decision. Always look at both columns before drawing conclusions.`,
    context: `**The Business Request**

Priya wants to know which acquisition channels bring higher-quality users. Not all channels are equal!

**The Concept**

Reuse the activation logic but add GROUP BY channel. Same metric definition, different segmentation.

**The Syntax**

\`\`\`sql
SELECT acquisition_channel, COUNT(DISTINCT user_id) AS total,
  COUNT(DISTINCT CASE WHEN activated THEN user_id END) AS activated,
  ROUND(100.0 * activated / total, 1) AS rate
FROM users LEFT JOIN events ON ...
GROUP BY acquisition_channel
ORDER BY rate DESC
\`\`\`

<div style="background: #f0f9ff; border-left: 4px solid #3b82f6; padding: 16px; margin: 16px 0; border-radius: 4px;">

**Your Task:**

For each channel, show \`total_users\`, \`activated_users\`, and \`activation_rate\`. Order by rate DESC.

</div>`,
    hint: `**Approach:**

1. Copy the activation query from Challenge 4
2. Add \`acquisition_channel\` to SELECT
3. Add GROUP BY acquisition_channel
4. ORDER BY activation_rate DESC to see best channels first

**Reusable Logic:** The activation definition stays the same - just segment it!`,
    solution: `SELECT 
  users.acquisition_channel,
  COUNT(DISTINCT users.user_id) AS total_users,
  COUNT(DISTINCT CASE 
    WHEN events.event_name IN ('place_viewed', 'place_saved', 'navigation_started')
      AND date_diff('hour', users.signup_at, events.event_time) <= 24
    THEN users.user_id 
  END) AS activated_users,
  ROUND(100.0 * COUNT(DISTINCT CASE 
    WHEN events.event_name IN ('place_viewed', 'place_saved', 'navigation_started')
      AND date_diff('hour', users.signup_at, events.event_time) <= 24
    THEN users.user_id 
  END) / COUNT(DISTINCT users.user_id), 1) AS activation_rate
FROM users
LEFT JOIN events ON users.user_id = events.user_id
GROUP BY users.acquisition_channel
ORDER BY activation_rate DESC`,
    tip: `Always look at BOTH rate AND volume. A channel with 80% activation but only 10 users is less valuable than 50% with 1000 users!`,
    explanation: `**Channel Quality Identified!**

**What Just Happened:**

1. **Same activation logic:** Reuse metric definition
2. **GROUP BY channel:** Segment by acquisition source
3. **Calculate rate per channel:** Compare quality

**How Channel Segmentation Works:**

\`\`\`sql
GROUP BY acquisition_channel  -- Same metric, different cuts
\`\`\`

**Typical Channel Performance:**

- **Referral (65%):** Highest quality - trusted recommendation
- **Organic (55%):** High intent - found you naturally
- **Paid (35%):** Lower intent - needs better targeting

**Strategic Decisions:**

- **If Organic 2x better than paid:** Invest in SEO, content, app store
- **If Referral highest:** Build referral program
- **If Paid low:** Fix targeting before spending more

**Pro Tip:** Look at BOTH rate AND volume. 80% with 10 users < 50% with 1000 users!`,
    concepts: [
      {
        icon: <FaChartLine className="text-3xl text-[#2563eb]" />,
        title: 'Channel Segmentation',
        description: 'Always segment metrics by acquisition channel. Reveals which marketing efforts bring quality users vs just volume.'
      },
      {
        icon: <FaBoxes className="text-3xl text-[#f97316]" />,
        title: 'Reusable Metric Logic',
        description: 'Define metrics once, then segment them. Same activation logic, different GROUP BY. Ensures consistency across reports.'
      }
    ]
  },
  {
    id: 'early-exploration-depth',
    title: 'Early Exploration Depth',
    question: `- Join \`users\` to \`events\` and count \`place_viewed\` events per user within their first 7 days
- Group users into buckets: \`'0 places'\`, \`'1-2 places'\`, \`'3-5 places'\`, \`'6+ places'\`
- Return \`place_view_bucket\` and \`users_count\`, ordered from lowest to highest bucket`,

    keyConcepts: ['COUNT DISTINCT', 'CASE WHEN', 'bucketing', '7-day window'],
    defaultQuery: `-- TIP: GROUP BY place_view_bucket to bucket users by exploration depth
-- COUNT(*) how many users fall into each bucket`,
    expectedColumns: [
      { name: 'place_view_bucket', type: 'TEXT' },
      { name: 'users_count', type: 'INTEGER' },
    ],
    businessRequest: `Priya wants to understand exploration depth in the first 7 days: are users discovering multiple places or just viewing one and leaving?`,
    concept: `**Bucketing with CASE WHEN**

When you have a raw number per user, a table with thousands of rows is hard to summarise. Bucketing collapses it into a handful of labelled groups so you can see the distribution at a glance.

\`CASE WHEN\` evaluates top-to-bottom and stops at the first match, which means you can write clean range checks:

\`\`\`sql
CASE
  WHEN count = 0  THEN '0'
  WHEN count <= 2 THEN '1-2'   -- only reached if count > 0
  WHEN count <= 5 THEN '3-5'   -- only reached if count > 2
  ELSE                 '6+'
END
\`\`\`

**Two-step approach:** calculate the per-user number in a CTE, then apply the bucketing in the outer query. Cramming both into one query gets messy fast.

**Sorting buckets:** \`ORDER BY\` on a text label sorts alphabetically - "6+" would sort before "0". Fix it with a \`CASE\` in the \`ORDER BY\` that maps each label to a sort position:

\`\`\`sql
ORDER BY CASE place_view_bucket WHEN '0 places' THEN 1 WHEN '1-2 places' THEN 2 ... END
\`\`\``,
    context: `**The Business Request**

Priya wants to understand exploration depth: Are users discovering multiple places or just viewing one?

**The Concept**

Count distinct places viewed per user in first 7 days, then bucket into ranges for easy analysis.

**The Syntax**

\`\`\`sql
WITH user_exploration AS (
  SELECT user_id, COUNT(DISTINCT CASE 
    WHEN event_name = 'place_viewed' AND date_diff('day', signup_at, event_time) <= 7
    THEN event_id END) AS places
  FROM users LEFT JOIN events ON ... GROUP BY user_id
)
SELECT CASE WHEN places = 0 THEN '0' WHEN places <= 2 THEN '1-2' ... END AS bucket,
  COUNT(*) AS users
FROM user_exploration GROUP BY bucket
\`\`\`

<div style="background: #f0f9ff; border-left: 4px solid #3b82f6; padding: 16px; margin: 16px 0; border-radius: 4px;">

**Your Task:**

Bucket users by places viewed in first 7 days: "0 places", "1-2 places", "3-5 places", "6+ places". Count users per bucket.

</div>`,
    hint: `**Step-by-step:**

1. Create CTE to count places viewed per user (first 7 days only)
2. Use CASE WHEN to bucket counts into ranges
3. GROUP BY bucket
4. Use CASE in ORDER BY for logical bucket order

**Key:** Filter for \`place_viewed\` events AND 7-day window (use \`date_diff('day', signup, event) <= 7\`).`,
    solution: `WITH user_exploration AS (
  SELECT 
    users.user_id,
    COUNT(DISTINCT CASE 
      WHEN events.event_name = 'place_viewed'
        AND date_diff('day', users.signup_at, events.event_time) <= 7
      THEN events.event_id
    END) AS places_viewed
  FROM users
  LEFT JOIN events ON users.user_id = events.user_id
  GROUP BY users.user_id
)
SELECT 
  CASE 
    WHEN places_viewed = 0 THEN '0 places'
    WHEN places_viewed BETWEEN 1 AND 2 THEN '1-2 places'
    WHEN places_viewed BETWEEN 3 AND 5 THEN '3-5 places'
    ELSE '6+ places'
  END AS place_view_bucket,
  COUNT(*) AS users_count
FROM user_exploration
GROUP BY place_view_bucket
ORDER BY 
  CASE place_view_bucket
    WHEN '0 places' THEN 1
    WHEN '1-2 places' THEN 2
    WHEN '3-5 places' THEN 3
    ELSE 4
  END`,
    tip: `Bucketing makes large datasets digestible. Instead of "user viewed 47 places", show distribution: 20% viewed 0, 30% viewed 1-2, etc.`,
    explanation: `**Exploration Behavior Analyzed!**

**What Just Happened:**

1. **COUNT DISTINCT places:** Unique places per user
2. **Filter first 7 days:** Focus on early behavior
3. **CASE WHEN buckets:** Group into ranges
4. **COUNT per bucket:** Distribution analysis

**How Bucketing Works:**

\`\`\`sql
CASE
  WHEN places = 0 THEN '0 places'
  WHEN places <= 2 THEN '1-2 places'
  WHEN places <= 5 THEN '3-5 places'
  ELSE '6+ places'
END
\`\`\`

**Bucket Interpretation:**

- **0 places:** Never engaged core feature (🚨 Activation failure)
- **1-2 places:** Minimal exploration (⚠️ Churn risk)
- **3-5 places:** Healthy exploration (✅ Finding value)
- **6+ places:** Power user behavior (🌟 High retention likely)

**Benchmark:** Successful discovery apps see 40%+ in "3-5" or "6+" buckets.

**If 60% in low buckets:** Core feature not discoverable - add prompts, recommendations, "places near you".`,
    concepts: [
      {
        icon: <FaBoxes className="text-3xl text-[#2563eb]" />,
        title: 'Bucketing for Distribution',
        description: 'Use CASE WHEN to group continuous values into buckets. Makes distributions readable and actionable for product decisions.'
      },
      {
        icon: <FaSearch className="text-3xl text-[#f97316]" />,
        title: '7-Day Windows',
        description: 'First 7 days are critical for habit formation. Use date_diff(\'day\', start, end) <= 7 to analyze early behavior patterns.'
      }]
  },
  {
    id: 'early-retention-proxy',
    title: 'Early Retention Proxy (Bonus)',
    question: `- Using \`users\` and \`sessions\`; a **returning user** is one with sessions on more than one distinct calendar day
- Return \`total_users\`, \`returning_users\`, and \`return_rate\` rounded to 1 decimal place`,

    keyConcepts: ['DATE()', 'MIN()', 'retention logic', 'multi-day activity'],
    defaultQuery: `-- TIP: Filter for sessions within 7 days of signup
-- DATEDIFF('day', signup_at, session_start) <= 7`,
    expectedColumns: [
      { name: 'total_users', type: 'INTEGER' },
      { name: 'returning_users', type: 'INTEGER' },
      { name: 'return_rate', type: 'DECIMAL' },
    ],
    businessRequest: `Priya needs the ultimate early retention signal: what percentage of users come back on a different calendar day? This predicts long-term product success better than any other early metric.`,
    concept: `**Counting distinct dates to detect returning users**

A user who opened the app twice in one hour and a user who opened it on Monday then again on Thursday look the same if you just count sessions. What distinguishes them is the number of *different calendar days* they were active.

\`\`\`sql
COUNT(DISTINCT DATE(session_start))  -- how many different calendar days?
\`\`\`

\`DATE()\` strips the time component so all sessions on the same day collapse to a single value. \`DISTINCT\` then de-duplicates them, giving you a count of unique days per user.

Once you have that number in a CTE, the outer query is straightforward: users with more than one distinct day returned on at least one *different* day. Anyone with exactly one distinct day - no matter how many sessions they had - only ever showed up on the same day they first signed in.`,
    context: `**The Business Request**

Priya needs the ultimate retention proxy: What percentage of users return on a different day? This predicts long-term success.

**The Concept**

Count distinct session dates per user. Users with 2+ different dates are "returning users."

**The Syntax**

\`\`\`sql
WITH user_sessions AS (
  SELECT user_id, COUNT(DISTINCT DATE(session_start)) AS distinct_days
  FROM sessions GROUP BY user_id
)
SELECT COUNT(*) AS total,
  COUNT(CASE WHEN distinct_days > 1 THEN 1 END) AS returning,
  ROUND(100.0 * returning / total, 1) AS return_rate
FROM user_sessions
\`\`\`

<div style="background: #f0f9ff; border-left: 4px solid #3b82f6; padding: 16px; margin: 16px 0; border-radius: 4px;">

**Your Task:**

Calculate \`total_users\`, \`returning_users\`, and \`return_rate\` (rounded to 1 decimal).

</div>`,
    hint: `**Approach:**

1. For each user, count distinct session dates (not timestamps!)
2. Users with > 1 distinct date are "returning users"
3. Calculate percentage of returning users

**Key Insight:** Use DATE() to convert timestamps to dates, then COUNT DISTINCT to find multi-day users.`,
    solution: `WITH user_sessions AS (
  SELECT 
    user_id,
    MIN(DATE(session_start)) AS first_session_date,
    COUNT(DISTINCT DATE(session_start)) AS distinct_days
  FROM sessions
  GROUP BY user_id
)
SELECT 
  COUNT(*) AS total_users,
  COUNT(CASE WHEN distinct_days > 1 THEN 1 END) AS returning_users,
  ROUND(100.0 * COUNT(CASE WHEN distinct_days > 1 THEN 1 END) / COUNT(*), 1) AS return_rate
FROM user_sessions`,
    tip: `Day 1 retention (return within 24 hours) is the strongest predictor of long-term retention. Track this metric daily!`,
    explanation: `**Early Retention Measured!**

**What Just Happened:**

1. **CTE: distinct session dates:** Find activity days per user
2. **COUNT(DISTINCT DATE(...)):** Count unique active days
3. **CASE WHEN > 1:** Identify returning users
4. **Calculate %:** Return rate

**How Return Rate Works:**

\`\`\`sql
100.0 * COUNT(CASE WHEN distinct_days > 1 THEN 1 END) / COUNT(*)
-- % of users with activity on more than one calendar day
\`\`\`

**Return Rate Benchmarks:**

- **< 20%:** 🚨 Critical problem (Urgent intervention)
- **20-40%:** ⚠️ Needs improvement (Optimize onboarding)
- **40-60%:** ✅ Healthy retention (Continue optimizing)
- **> 60%:** 🌟 Excellent (Strong product-market fit)

**Why Day 1 Retention Matters:**

- **Return on Day 2:** 10x more likely active at Day 30
- **Return on Day 2:** 20x more likely to become paying

🎉 **Case Complete!** You've mastered activation and retention analytics - the core metrics every product team tracks.`,
    concepts: [
      {
        icon: <FaTrophy className="text-3xl text-[#f97316]" />,
        title: 'Retention Logic',
        description: 'Count distinct activity dates to measure multi-day engagement. Day 1 retention is the strongest predictor of long-term success.'
      },
      {
        icon: <FaChartLine className="text-3xl text-[#2563eb]" />,
        title: 'Product Health Metrics',
        description: 'Activation + Retention = Product Health. Track both to understand if users find value (activate) and stick around (retain).'
      }
    ]
  }
];

export const analyticsConcepts = [
  {
    icon: <FaDatabase className="text-3xl text-[#2563eb]" />,
    title: 'Product Data Model',
    description: 'Product analytics uses users → sessions → events hierarchy. Master this relationship to answer any product question.'
  },
  {
    icon: <FaTrophy className="text-3xl text-[#f97316]" />,
    title: 'Activation Metrics',
    description: 'Define activation as experiencing core value within a time window. This predicts retention better than any other metric.'
  },
  {
    icon: <FaClock className="text-3xl text-[#10b981]" />,
    title: 'Time Windows',
    description: 'Use date_diff() for time arithmetic. date_diff(\'hour\', start, end) is perfect for 24-hour windows.'
  },
  {
    icon: <FaChartLine className="text-3xl text-[#8b5cf6]" />,
    title: 'Retention Analysis',
    description: 'Count distinct activity dates to measure multi-day engagement. Day 1 retention is the strongest predictor of long-term success.'
  }
];
